import { useState, useEffect } from 'react';

interface DynamicFormProps {
  schema: any;
  uiConfig?: any;
  formData?: any;
  onSubmit: (data: any, isDraft?: boolean) => void;
  onCancel?: () => void;
  loading?: boolean;
}

export function DynamicForm({ 
  schema, 
  uiConfig = {}, 
  formData: initialFormData, 
  onSubmit, 
  onCancel, 
  loading 
}: DynamicFormProps) {
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    if (initialFormData) {
      setFormData(initialFormData);
    } else if (schema) {
      // デフォルト値を設定
      const defaultValues: any = {};
      Object.entries(schema.properties || {}).forEach(([key, field]: [string, any]) => {
        if (field.default !== undefined) {
          defaultValues[key] = field.default;
        }
      });
      setFormData(defaultValues);
    }
  }, [initialFormData, schema]);

  const validateField = (name: string, value: any, field: any): string | null => {
    const required = schema?.required?.includes(name) || false;
    
    if (required && (!value || value.toString().trim() === '')) {
      return `${field.title}は必須です`;
    }
    
    if (value && field.type === 'string' && field.maxLength && value.length > field.maxLength) {
      return `${field.title}は${field.maxLength}文字以内で入力してください`;
    }
    
    if (value && field.type === 'number') {
      const numValue = Number(value);
      if (isNaN(numValue)) {
        return `${field.title}は数値で入力してください`;
      }
      if (field.minimum !== undefined && numValue < field.minimum) {
        return `${field.title}は${field.minimum}以上で入力してください`;
      }
      if (field.maximum !== undefined && numValue > field.maximum) {
        return `${field.title}は${field.maximum}以下で入力してください`;
      }
    }
    
    return null;
  };

  const handleInputChange = (name: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [name]: value }));
    
    // リアルタイムバリデーション
    const field = schema?.properties?.[name];
    if (field) {
      const error = validateField(name, value, field);
      setErrors(prev => ({ ...prev, [name]: error || '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent, isDraft = false) => {
    e.preventDefault();
    
    if (!isDraft) {
      // 通常の申請時はバリデーション実行
      const newErrors: {[key: string]: string} = {};
      
      Object.entries(schema?.properties || {}).forEach(([name, field]: [string, any]) => {
        const error = validateField(name, formData[name], field);
        if (error) {
          newErrors[name] = error;
        }
      });
      
      setErrors(newErrors);
      
      if (Object.keys(newErrors).some(key => newErrors[key])) {
        return;
      }
    }
    
    onSubmit(formData, isDraft);
  };

  const renderField = (name: string, field: any) => {
    const required = schema?.required?.includes(name) || false;
    const error = errors[name];
    const value = formData[name] || '';
    const fieldUI = uiConfig[name] || {};

    const baseInputClass = `form-input ${
      error ? 'border-red-500' : ''
    }`;

    // UI設定に基づいてウィジェットを決定
    const widget = fieldUI['ui:widget'] || 'default';
    const rows = fieldUI['ui:rows'] || 4;
    const placeholder = fieldUI['ui:placeholder'] || field.title;

    switch (field.type) {
      case 'string':
        if (field.enum) {
          // ラジオボタンまたはセレクトボックス
          if (widget === 'radio') {
            return (
              <div key={name} className="form-group">
                <label className="form-label">
                  {field.title} {required && <span style={{ color: '#ef4444' }}>*</span>}
                </label>
                <div className="space-y-2">
                  {field.enum.map((option: string) => (
                    <label key={option} className="flex items-center">
                      <input
                        type="radio"
                        name={name}
                        value={option}
                        checked={value === option}
                        onChange={(e) => handleInputChange(name, e.target.value)}
                        className="mr-2"
                      />
                      {option}
                    </label>
                  ))}
                </div>
                {error && <p className="text-sm text-red-500">{error}</p>}
              </div>
            );
          } else {
            return (
              <div key={name} className="form-group">
                <label className="form-label">
                  {field.title} {required && <span style={{ color: '#ef4444' }}>*</span>}
                </label>
                <select
                  value={value}
                  onChange={(e) => handleInputChange(name, e.target.value)}
                  className={baseInputClass}
                  style={{ maxWidth: '300px' }}
                >
                  <option value="">選択してください</option>
                  {field.enum.map((option: string) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {error && <p className="text-sm text-red-500">{error}</p>}
              </div>
            );
          }
        } else if (field.format === 'date' || widget === 'date') {
          // 日付入力
          return (
            <div key={name} className="form-group">
              <label className="form-label">
                {field.title} {required && <span style={{ color: '#ef4444' }}>*</span>}
              </label>
              <input
                type="date"
                value={value}
                onChange={(e) => handleInputChange(name, e.target.value)}
                className={baseInputClass}
                style={{ maxWidth: '300px' }}
              />
              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
          );
        } else if (field.format === 'time' || widget === 'time') {
          // 時刻入力
          return (
            <div key={name} className="form-group">
              <label className="form-label">
                {field.title} {required && <span style={{ color: '#ef4444' }}>*</span>}
              </label>
              <input
                type="time"
                value={value}
                onChange={(e) => handleInputChange(name, e.target.value)}
                className={baseInputClass}
                style={{ maxWidth: '300px' }}
                min={field['x-timeMinimum']}
                max={field['x-timeMaximum']}
                step={fieldUI['ui:options']?.step}
              />
              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
          );
        } else if (field.format === 'date-time' || widget === 'datetime') {
          // 日時入力
          return (
            <div key={name} className="form-group">
              <label className="form-label">
                {field.title} {required && <span style={{ color: '#ef4444' }}>*</span>}
              </label>
              <input
                type="datetime-local"
                value={value}
                onChange={(e) => handleInputChange(name, e.target.value)}
                className={baseInputClass}
                style={{ maxWidth: '300px' }}
                min={field.formatMinimum}
                max={field.formatMaximum}
              />
              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
          );
        } else if (widget === 'textarea' || field.maxLength > 100) {
          // テキストエリア
          return (
            <div key={name} className="form-group">
              <label className="form-label">
                {field.title} {required && <span style={{ color: '#ef4444' }}>*</span>}
              </label>
              <textarea
                value={value}
                onChange={(e) => handleInputChange(name, e.target.value)}
                className={baseInputClass}
                rows={rows}
                maxLength={field.maxLength}
                placeholder={placeholder}
              />
              {field.maxLength && (
                <p className="text-xs text-gray-500">
                  {value.length}/{field.maxLength}文字
                </p>
              )}
              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
          );
        } else {
          // 通常のテキスト入力
          return (
            <div key={name} className="form-group">
              <label className="form-label">
                {field.title} {required && <span style={{ color: '#ef4444' }}>*</span>}
              </label>
              <input
                type="text"
                value={value}
                onChange={(e) => handleInputChange(name, e.target.value)}
                className={baseInputClass}
                maxLength={field.maxLength}
                placeholder={placeholder}
              />
              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
          );
        }

      case 'number':
        return (
          <div key={name} className="form-group">
            <label className="form-label">
              {field.title} {required && <span style={{ color: '#ef4444' }}>*</span>}
            </label>
            <input
              type="number"
              value={value}
              onChange={(e) => handleInputChange(name, e.target.value)}
              className={baseInputClass}
              style={{ maxWidth: '300px' }}
              min={field.minimum}
              max={field.maximum}
              placeholder={placeholder}
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'boolean':
        return (
          <div key={name} className="form-group">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={value}
                onChange={(e) => handleInputChange(name, e.target.checked)}
                className="form-checkbox mr-2"
              />
              {field.title} {required && <span style={{ color: '#ef4444' }}>*</span>}
            </label>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'array':
        // 複数選択（checkboxes）
        if (widget === 'checkboxes' && field.items?.enum) {
          const arrayValue = Array.isArray(value) ? value : [];
          const options = field.items.enum;
          const optionLabels = field.items.enumNames || options;

          return (
            <div key={name} className="form-group">
              <label className="form-label">
                {field.title} {required && <span style={{ color: '#ef4444' }}>*</span>}
              </label>
              <div className="space-y-2">
                {options.map((option: string, idx: number) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={arrayValue.includes(option)}
                      onChange={(e) => {
                        const newValue = e.target.checked
                          ? [...arrayValue, option]
                          : arrayValue.filter((v: string) => v !== option);
                        handleInputChange(name, newValue);
                      }}
                      className="form-checkbox mr-2"
                    />
                    {optionLabels[idx] || option}
                  </label>
                ))}
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
          );
        }
        // その他のarray型（未対応）
        return (
          <div key={name} className="form-group">
            <label className="form-label">
              {field.title} {required && <span style={{ color: '#ef4444' }}>*</span>}
            </label>
            <p className="text-sm text-gray-500">この配列フィールドはサポートされていません</p>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      default:
        return (
          <div key={name} className="form-group">
            <label className="form-label">
              {field.title} {required && <span style={{ color: '#ef4444' }}>*</span>}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => handleInputChange(name, e.target.value)}
              className={baseInputClass}
              placeholder={placeholder}
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );
    }
  };

  if (!schema) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">フォームの読み込みに失敗しました</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {schema.title || '申請フォーム'}
        </h2>
        {schema.description && (
          <p className="text-gray-600">{schema.description}</p>
        )}
      </div>

      <form className="space-y-4">
        {(() => {
          // ui:orderが指定されている場合はその順番で表示
          const properties = schema.properties || {};
          const uiOrder = uiConfig['ui:order'];
          
          if (uiOrder && Array.isArray(uiOrder)) {
            // ui:orderに従って並び替え
            return uiOrder
              .filter(name => properties[name]) // 存在するプロパティのみ
              .map(name => renderField(name, properties[name]));
          } else {
            // ui:orderがない場合は通常通り（ただし順番は保証されない）
            return Object.entries(properties).map(([name, field]) =>
              renderField(name, field)
            );
          }
        })()}

        <div className="flex justify-end space-x-4 pt-6 border-t">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary"
              disabled={loading}
            >
              キャンセル
            </button>
          )}
          <button
            type="button"
            onClick={(e) => handleSubmit(e, true)}
            className="btn btn-secondary"
            disabled={loading}
          >
            {loading ? '保存中...' : '下書き保存'}
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e, false)}
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? '申請中...' : '申請提出'}
          </button>
        </div>
      </form>
    </div>
  );
}